import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from '../../i18n';

/**
 * <cadence-modal ?open=${} heading="…"> with default slot for body and a
 * "footer" slot for actions. Emits `close` on overlay click, Escape, or the
 * close button. An outside click only dismisses when both the press and the
 * release land on the overlay — a drag that starts inside (e.g. selecting
 * text in a field) and releases outside will not close the dialog.
 */
@customElement('cadence-modal')
export class CadenceModal extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property() heading = '';

  static styles = css`
    :host { display: none; }
    :host([open]) { display: block; }
    .overlay {
      position: fixed; inset: 0; background: rgba(2, 6, 23, 0.55);
      display: grid; place-items: center; padding: var(--space-4); z-index: 900;
    }
    .dialog {
      background: var(--color-bg-primary); color: var(--color-text-primary);
      border-radius: var(--radius-lg); box-shadow: var(--shadow-lg);
      width: 100%; max-width: 480px; max-height: 90vh; overflow: auto;
    }
    header {
      display: flex; align-items: center; justify-content: space-between;
      padding: var(--space-4) var(--space-5); border-bottom: 1px solid var(--color-border);
    }
    header h2 { margin: 0; font-size: 1.1rem; }
    .close { background: none; border: none; cursor: pointer; font-size: 1.4rem; color: var(--color-text-secondary); line-height: 1; }
    .body { padding: var(--space-5); }
    .footer { padding: var(--space-4) var(--space-5); border-top: 1px solid var(--color-border); display: flex; gap: 10px; justify-content: flex-end; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this._onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && this.open) this.close(); };
    window.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this._onKey);
  }

  private _onKey!: (e: KeyboardEvent) => void;
  /** True only while a pointer press that began on the overlay itself is in flight. */
  private _pressedOnOverlay = false;

  private close() {
    this.dispatchEvent(new CustomEvent('close'));
  }

  render() {
    return html`
      <div
        class="overlay"
        @mousedown=${(e: Event) => { this._pressedOnOverlay = e.target === e.currentTarget; }}
        @click=${(e: Event) => { if (e.target === e.currentTarget && this._pressedOnOverlay) this.close(); }}
      >
        <div class="dialog" role="dialog" aria-modal="true" aria-label=${this.heading}>
          <header>
            <h2>${this.heading}</h2>
            <button class="close" aria-label=${t('ui.modal.close')} @click=${this.close}>&times;</button>
          </header>
          <div class="body"><slot></slot></div>
          <div class="footer"><slot name="footer"></slot></div>
        </div>
      </div>
    `;
  }
}
